import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, ExternalLinkIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/hnrT5ABaZYTkoMWzFC51a8/Logo_and_Teashirt_branding.png';
const WEBINAR_URL = 'https://thenetworkingsalon.live/green';
interface NavLink {
  label: string;
  href: string;
  external?: boolean;
}
const navLinks: NavLink[] = [{
  label: 'Home',
  href: '#top'
}, {
  label: 'About',
  href: '#about'
}, {
  label: 'Memberships',
  href: '#memberships'
}, {
  label: 'Webinar',
  href: WEBINAR_URL,
  external: true
}];
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const handleNavClick = (e: React.MouseEvent, link: NavLink) => {
    if (link.external) return; // let the anchor open in a new tab
    e.preventDefault();
    setMobileOpen(false);
    if (link.href === '#top') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    const id = link.href.replace('#', '');
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? 'bg-cream/95 backdrop-blur-md shadow-sm border-b border-forest/10' : 'bg-cream/80 backdrop-blur-sm'}`} data-id="element-45">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 sm:h-20" data-id="element-46">
        {/* Logo */}
        <a href="#top" onClick={e => handleNavClick(e, {
        label: 'Home',
        href: '#top'
      })} className="flex items-center gap-2 shrink-0" aria-label="The Networking Salon — Home" data-id="element-47">
          <img src={LOGO_URL} alt="The Networking Salon" className="h-9 sm:h-11 w-auto object-contain" width={240} height={64} decoding="async" loading="eager" data-id="element-48" />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" data-id="element-49">
          {navLinks.map(link => link.external ? <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="group px-4 py-2 text-sm font-medium text-forest hover:text-sage transition-colors inline-flex items-center gap-1.5" data-id="element-50">
                {link.label}
                <ExternalLinkIcon size={14} className="opacity-60 group-hover:opacity-100 transition-opacity" data-id="element-51" />
              </a> : <a key={link.label} href={link.href} onClick={e => handleNavClick(e, link)} className="px-4 py-2 text-sm font-medium text-forest hover:text-sage transition-colors" data-id="element-52">
                {link.label}
              </a>)}
          <a href="#memberships" onClick={e => handleNavClick(e, {
          label: 'Join',
          href: '#memberships'
        })} className="ml-3 bg-forest text-cream px-5 py-2.5 text-sm font-medium hover:bg-forest-light transition-colors" data-id="element-53">
            Join the Salon
          </a>
        </nav>

        {/* Mobile toggle */}
        <button onClick={() => setMobileOpen(v => !v)} className="md:hidden text-forest p-2 -mr-2" aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen} data-id="element-54">
          {mobileOpen ? <XIcon size={24} data-id="element-55" /> : <MenuIcon size={24} data-id="element-56" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence data-id="element-57">
        {mobileOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="md:hidden bg-cream border-t border-forest/10 overflow-hidden" data-id="element-58">
            <nav className="px-6 py-4 flex flex-col" data-id="element-59">
              {navLinks.map(link => link.external ? <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="py-3 text-base font-medium text-forest border-b border-forest/10 inline-flex items-center justify-between" data-id="element-60">
                    {link.label}
                    <ExternalLinkIcon size={16} className="text-sage" data-id="element-61" />
                  </a> : <a key={link.label} href={link.href} onClick={e => handleNavClick(e, link)} className="py-3 text-base font-medium text-forest border-b border-forest/10" data-id="element-62">
                    {link.label}
                  </a>)}
              <a href="#memberships" onClick={e => handleNavClick(e, {
            label: 'Join',
            href: '#memberships'
          })} className="mt-4 bg-forest text-cream px-5 py-3 text-sm font-medium text-center hover:bg-forest-light transition-colors" data-id="element-63">
                Join the Salon
              </a>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}